import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CinzelSelect.css';

/**
 * Drop-in replacement for a native <select> that renders its own popup, so the
 * option list uses the app's Cinzel font. Native <select> popups are drawn by
 * the OS and ignore web fonts (falling back to a generic serif/cursive).
 *
 * API mirrors <select>: pass <option> children and an onChange handler. onChange
 * receives a synthetic `{ target: { name, value } }` so existing handlers that
 * read `e.target.value` / `e.target.name` keep working unchanged.
 */
const CinzelSelect = ({ name, id, value, onChange, className = '', disabled = false, children }) => {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const options = useMemo(
        () =>
            React.Children.toArray(children)
                .filter((c) => React.isValidElement(c) && c.type === 'option')
                .map((c) => ({
                    value: c.props.value == null ? '' : String(c.props.value),
                    label: c.props.children,
                    disabled: !!c.props.disabled,
                })),
        [children]
    );

    const valueStr = value == null ? '' : String(value);
    const selected = options.find((o) => o.value === valueStr);

    const positionMenu = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.bottom, width: r.width });
    }, []);

    const closeMenu = useCallback(() => setOpen(false), []);
    const openMenu = useCallback(() => {
        if (disabled) return;
        positionMenu();
        setOpen(true);
    }, [disabled, positionMenu]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                closeMenu();
            }
        };
        const onReposition = () => positionMenu();
        const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
        document.addEventListener('mousedown', onPointerDown);
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, closeMenu, positionMenu]);

    const emit = (optValue) => {
        if (onChange) onChange({ target: { name, value: optValue } });
    };

    const choose = (opt) => {
        if (opt.disabled) return;
        closeMenu();
        if (opt.value !== valueStr) emit(opt.value);
    };

    const moveSelection = (dir) => {
        const start = options.findIndex((o) => o.value === valueStr);
        let i = start;
        for (let step = 0; step < options.length; step++) {
            i = Math.min(options.length - 1, Math.max(0, i + dir));
            if (i === start) break;
            if (options[i] && !options[i].disabled) { emit(options[i].value); return; }
            if (i === 0 || i === options.length - 1) break;
        }
    };

    const onTriggerKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) { openMenu(); return; }
            moveSelection(e.key === 'ArrowDown' ? 1 : -1);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open ? closeMenu() : openMenu();
        }
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                id={id}
                className={`cinzel-select-trigger ${className} ${open ? 'is-open' : ''}`.trim()}
                onClick={() => (open ? closeMenu() : openMenu())}
                onKeyDown={onTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="cinzel-select-value">{selected ? selected.label : ''}</span>
                <span className="cinzel-select-arrow" aria-hidden="true">▾</span>
            </button>
            {open && rect && createPortal(
                <ul
                    ref={menuRef}
                    className="cinzel-select-menu"
                    role="listbox"
                    style={{ left: rect.left, top: rect.top, minWidth: rect.width }}
                >
                    {options.map((opt, i) => (
                        <li
                            key={`${opt.value}-${i}`}
                            role="option"
                            aria-selected={opt.value === valueStr}
                            className={
                                'cinzel-select-option' +
                                (opt.value === valueStr ? ' is-selected' : '') +
                                (opt.disabled ? ' is-disabled' : '')
                            }
                            onClick={() => choose(opt)}
                        >
                            {opt.label}
                        </li>
                    ))}
                </ul>,
                document.body
            )}
        </>
    );
};

export default CinzelSelect;
